import { emptyBelgeler, type PersonelFormValues } from './schemas/personelFormSchema'
import type { BelgeSatir, BordroDurum, GecmisItem, HedefSatir, IzinTalep, OdemeSatir, Personel } from './types'

export type PersonelProfile = {
  ad: string
  dogum: string
  tckn: string
  cinsiyet: 'erkek' | 'kadin' | 'diger'
  medeni: 'bekar' | 'evli' | 'bosanmis' | 'dul'
  uyruk: string
  foto_adi: string
  departman_id: string
  departman: string
  pozisyon: string
  sube: string
  istihdam: 'tam_zamanli' | 'yari_zamanli' | 'stajyer' | 'sozlesmeli'
  /** Bordroda prim kesintisini belirler: SGK (4/a), Bağkur (4/b, kişi öder) ya da prim ödenmeyen emekli. */
  sigorta: 'sgk' | 'bagkur' | 'emekli'
  /** Firma ortağı (bordroyu değiştirmez; prim durumu `sigorta` alanından gelir). */
  ortak: boolean
  /** Ortaklık payı yüzde ("33.33"); boş olabilir. */
  ortaklik_payi: string
  /** Emeklilik tarihi (ISO) ve dayanak notu (emekli sicil no, SGK yazısı…). Prim ödenmeyen emeklide tarih zorunlu. */
  emeklilik_tarihi: string
  emeklilik_notu: string
  calisma_turu: Personel['calisma_turu']
  ise_giris: string
  calisma_baslangic: string
  calisma_bitis: string
  kidem: string
  yonetici: string
  lokasyon: string
  brut_maas: string
  egitim_durumu: 'lise' | 'onlisans' | 'lisans' | 'yuksek_lisans' | 'doktora'
  universite_id: string
  mezun_bolum: string
  egitim: string
  durum: Personel['durum']
  sicil: string
  email: string
  telefon: string
  is_tel: string
  il_id: string
  ilce_id: string
  adres: string
  acil_ad: string
  acil_yakinlik: string
  acil_telefon: string
  acil_email: string
  acil_adres: string
  panel_erisim: boolean
  firma_email: string
  belgeler: { tip: string; dosya_adi: string }[]
  positionId: string
  /** Yönetici personel id (form seçicisi); `yonetici` görünen addır. */
  managerId: string
  il_adi: string
  ilce_adi: string
  /** Kayıtlı en son brüt ücret (değişiklik tespiti: yalnız farklıysa yeni ücret kaydı açılır). */
  brut_kayitli: string
  /** Çalışma takvimi kayıtlı mı (saat değişikliğinde mevcut günler korunur). */
  takvim: WorkScheduleDay[]
}

export type WorkScheduleDay = {
  weekday: number
  is_working_day: boolean
  start_time?: string
  end_time?: string
  end_day_offset: number
  break_minutes: number
  timezone: string
}

type Amount = { label: string; amount: string }

export type PersonelDetail = {
  gecmis: GecmisItem[]
  profil: { pct: number; checklist: { key: string; done: boolean }[] }
  ucret: {
    brut: string
    net: string
    yan: string
    kesintiToplam: string
    iseveren: string
    kazanc: Amount[]
    kesinti: Amount[]
    /** period: YYYY-MM (API), donem: görünen etiket. */
    gecmis: { id: string; period: string; donem: string; brut: string; net: string; durum: BordroDurum; gecmisAktarim: boolean }[]
  }
  izin: {
    kpi: { yillik_hak: string; kullanilan: string; kalan: string; mesai: string }
    talepler: IzinTalep[]
    puantaj: { tarih: string; tur: string; sure: string; durum: 'onaylandi' | 'bekliyor' | 'red' }[]
    izinGunleri: string[]
    onay: { title: string; at: string; by: string; state: 'done' | 'act' }[]
  }
  belge: {
    liste: (BelgeSatir & { bitis?: string })[]
    ozet: { pct: number; tamam: number; eksik: number; suresi_gecen: number }
    eksik: { id: string; ad: string }[]
    yaklasan: { id: string; ad: string; tarih: string; kalan: string }[]
    sozlesme: { tip: string; baslangic: string; bitis: string; deneme: string; istihdam: PersonelProfile['istihdam'] }
  }
  odeme: {
    yilToplam: string
    yilAdet: number
    bekleyen: string
    bekleyenAdet: number
    avans: string
    avansAdet: number
    liste: (OdemeSatir & { aciklama: string; yontem: string; gecmisAktarim?: boolean })[]
    timeline: { t: string; b: string }[]
    banka: { ad: string; iban: string }
  }
  perf: {
    kpi: {
      skor: string
      skorDeger: number
      skor_tarih: string
      hedefler: string
      hedef_yil: string
      egitim_tamam: string
      egitim_plan: string
      sonraki: string
    }
    hedefler: (HedefSatir & { son_tarih: string; aciklama: string })[]
    notlar: { id: string; metin: string; yazar: string; unvan: string; tarih: string }[]
    yetkinlikler: { id?: string; ad: string; skor: string; pct: number }[]
    egitim: { id?: string; t: string; b: string; a: string; badge: 'Sertifika' | 'Planlandı'; state: 'done' | 'planned' }[]
  }
}

export function profileToFormValues(profile: PersonelProfile): PersonelFormValues {
  return {
    ad: profile.ad,
    dogum: profile.dogum,
    tckn: profile.tckn,
    cinsiyet: profile.cinsiyet,
    medeni: profile.medeni,
    uyruk: profile.uyruk,
    foto_adi: profile.foto_adi,
    foto_dosya: null,
    sicil: profile.sicil,
    durum: profile.durum,
    departman_id: profile.departman_id,
    pozisyon: profile.positionId,
    sube: profile.sube,
    istihdam: profile.istihdam,
    sigorta: profile.sigorta,
    ortak: profile.ortak,
    ortaklik_payi: profile.ortaklik_payi,
    emeklilik_tarihi: profile.emeklilik_tarihi,
    emeklilik_notu: profile.emeklilik_notu,
    calisma_turu: profile.calisma_turu,
    ise_giris: profile.ise_giris,
    calisma_baslangic: profile.calisma_baslangic,
    calisma_bitis: profile.calisma_bitis,
    yonetici: profile.managerId,
    brut_maas: profile.brut_maas,
    maas_gecerlilik: '',
    egitim_durumu: profile.egitim_durumu,
    universite_id: profile.universite_id,
    mezun_bolum: profile.mezun_bolum,
    email: profile.email,
    telefon: profile.telefon,
    is_tel: profile.is_tel,
    il_id: profile.il_id,
    ilce_id: profile.ilce_id,
    adres: profile.adres,
    acil_ad: profile.acil_ad,
    acil_yakinlik: profile.acil_yakinlik,
    acil_telefon: profile.acil_telefon,
    acil_email: profile.acil_email,
    acil_adres: profile.acil_adres,
    panel_erisim: profile.panel_erisim,
    firma_email: profile.firma_email,
    role_ids: [],
    mailbox_assign: false,
    mailbox_provider_id: '',
    mailbox_secret: '',
    mailbox_signature: '',
    sifre: '',
    sifre_tekrar: '',
    sifre_sifirla: false,
    belgeler: emptyBelgeler(),
  }
}
